"use client"
import { useState } from "react"
import type { Overview } from "@/lib/review/overview"
import type { Summary } from "@/lib/review/overview"
import { PrStatusBoard } from "./pr-status-board"

const breakdown = (s: Summary) =>
  [
    Object.entries(s.byLocale)
      .map(([l, n]) => `${l} ${n}`)
      .join(", "),
    Object.entries(s.byTier)
      .map(([t, n]) => `${t} ${n}`)
      .join(", "),
  ]
    .filter(Boolean)
    .join(" · ")

/**
 * Backlog summary + who-can-help, shown as a header above the reviewer's own
 * worklist (inside the toolbar's Review tab). Leads with the viewer's OWN
 * in-scope backlog (the only strings they can action) and demotes the team-wide
 * total to a labelled secondary line — so a reviewer isn't shown thousands of
 * out-of-scope strings, and an empty-scope viewer isn't told there's anything
 * "to review". Who-can-help stays visible to everyone so whoever opened the
 * check knows who to ping. Purely presentational — the worklist fetches
 * `/api/review/overview` and passes the data in.
 */
export function ReviewOverviewHeader({ overview }: { overview: Overview }) {
  const { backlog, scoped, whoCanHelp, viewerScope, blocked, localeStatus } =
    overview
  const [copied, setCopied] = useState(false)
  const hasScope = viewerScope.locales.length > 0

  function copyLink() {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <div className="ahead-review-overview">
      {hasScope ? (
        scoped.total === 0 ? (
          <p className="ahead-review-overview-total">
            🎉 Your scope is clear — nothing to review.
          </p>
        ) : (
          <>
            <p className="ahead-review-overview-total">
              {scoped.total} in your scope
            </p>
            <p className="ahead-review-overview-breakdown">
              {breakdown(scoped)}
            </p>
          </>
        )
      ) : null}
      <p className="ahead-review-overview-team">
        {backlog.total === 0
          ? "🎉 Team backlog clear — all caught up."
          : `Team-wide: ${backlog.total} need review — ${breakdown(backlog)}`}
      </p>
      {blocked.length ? (
        <div className="ahead-review-blocked">
          {blocked.map((b) => (
            <p key={b.locale} className="ahead-review-blocked-row">
              ⚠ {b.count ? `${b.count} ` : ""}
              {b.locale} string{b.count === 1 ? "" : "s"} hidden — {b.market}{" "}
              isn’t public yet.{" "}
              {b.previewUrl ? (
                <a href={b.previewUrl}>Open the {b.market} preview link</a>
              ) : (
                <span>Ask an admin for the {b.market} preview link</span>
              )}{" "}
              to review them.
            </p>
          ))}
        </div>
      ) : null}
      {whoCanHelp.length ? (
        <PrStatusBoard
          rows={localeStatus}
          reviewLink={typeof window === "undefined" ? "" : window.location.href}
        />
      ) : null}
      <button type="button" onClick={copyLink} aria-live="polite">
        {copied ? "✓ Copied" : "Copy review link"}
      </button>
    </div>
  )
}
